import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const PLATFORMS = [
  { code: 'SMARTSTORE', label: '스마트스토어' },
  { code: 'ELEVENST', label: '11번가' },
  { code: 'GMARKET', label: '지마켓' },
  { code: 'AUCTION', label: '옥션' },
  { code: 'SELLERHUB', label: '셀러허브' },
  { code: 'COUPANG', label: '쿠팡' },
  { code: 'TOSS', label: '토스쇼핑' },
];
// GMARKET/AUCTION 둘 다 ESM 엑셀 업로드 방식(같은 send-config 저장소, targetSites만 다름)을 공유한다.
// ESM 전용 기능(미등록항목 자동채우기 - GET/PUT API 방식)은 이 배열만 참조한다.
const ESM_PLATFORM_CODES = ['GMARKET', 'AUCTION'];
// [설계 원칙 - 2026-09-04] "엑셀 다운로드/확인파일 업로드" UI는 ESM/셀러허브가 공유하지만,
// 내부 처리(파일 파싱, 엑셀 생성 로직)는 판매처별로 다르다. 확장 시(향후 다른 엑셀 판매처 추가)
// 이 배열에만 코드를 추가하면 UI가 자동으로 반영되도록 ESM_PLATFORM_CODES와 분리해서 관리한다.
const EXCEL_PLATFORM_CODES = [...ESM_PLATFORM_CODES, 'SELLERHUB'];

const SEND_ENDPOINT = {
  SMARTSTORE: 'send-to-smartstore',
  ELEVENST: 'send-to-eleven',
};

const TABS = [
  { key: 'unsent', label: '미전송' },
  { key: 'pending', label: '전송중' },
  { key: 'sent', label: '전송완료' },
  { key: 'failed', label: '전송실패' },
  { key: 'excluded', label: '전송제외' },
];

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function categoryPath(p) {
  return [p.large_category, p.middle_category, p.small_category, p.detail_category].filter(Boolean).join(' > ') || '-';
}
// 전송용 카테고리 오버라이드 검색 셀. 텍스트 직접 입력이 아니라 키워드 검색 -> 후보 선택 방식
// (오픈마켓 매칭 페이지와 같은 검색 API 재사용). 선택 시 product_platform_send_config에만 저장하고
// 우리 categories/category_platform_mappings 원본은 건드리지 않는다.
function CategoryOverrideCell({ product, platform, cfg, onSave, canOverride, api, matchedName }) {
  const [editing, setEditing] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const platformKeyMap = { GMARKET: 'esm', AUCTION: 'esm', ELEVENST: 'eleven', SELLERHUB: 'sellerhub', COUPANG: 'coupang', TOSS: 'toss' };
  const searchPlatform = platformKeyMap[platform];
  const overridden = cfg.category_code && cfg.category_name;
  // [기능 변경 - 2026-10-22] 우리 카테고리와 "이 판매처에 매칭된(또는 직접 지정한) 카테고리"를 두 줄로 함께 보여준다.
  // 스마트스토어는 우리 카테고리가 스마트스토어 기준이라 한 줄만 보여준다.
  if (platform === 'SMARTSTORE') return <span>{categoryPath(product)}</span>;
  const PLATFORM_LABEL = { ELEVENST: '11번가', GMARKET: '지마켓', AUCTION: '옥션', COUPANG: '쿠팡', SELLERHUB: '셀러허브', TOSS: '토스쇼핑' };
  const label = PLATFORM_LABEL[platform] || platform;
  const labelStyle = { display: 'inline-block', width: '56px', color: '#aaa', fontSize: '11px', fontWeight: '400' };
  const ourLine = (
    <div style={{ color: '#888', lineHeight: '1.6' }}>
      <span style={labelStyle}>우리</span>{categoryPath(product)}
    </div>
  );
  const platformText = overridden ? cfg.category_name : matchedName;
  const platformTextStyle = overridden
    ? { color: '#185FA5', fontWeight: '600' }
    : (matchedName ? { color: '#888' } : { color: '#A32D2D' });
  if (!canOverride) {
    return (
      <div>
        {ourLine}
        <div style={{ lineHeight: '1.6', ...platformTextStyle }}>
          <span style={labelStyle}>{label}</span>{platformText || '매칭 없음'}
        </div>
      </div>
    );
  }
  if (!editing) {
    return (
      <div>
        {ourLine}
        <div style={{ lineHeight: '1.6' }}>
          <span style={labelStyle}>{label}</span>
          <span onClick={() => setEditing(true)}
            style={{ cursor: 'pointer', borderBottom: '1px dashed #ccc', ...platformTextStyle }}
            title="클릭하여 이 판매처 전송 시에만 적용될 카테고리를 검색/지정 (원본 카테고리는 변경되지 않음)">
            {platformText || '매칭 없음'}
          </span>
          {overridden && (
            <button onClick={() => onSave({ category_code: null, category_name: null })}
              title="이 판매처 전송용으로 지정한 카테고리만 해제합니다 (우리 카테고리와 매칭 원본은 바뀌지 않습니다)"
              style={{ fontSize: '10px', marginLeft: '6px', padding: '1px 6px', borderRadius: '4px', border: '0.5px solid #ccc', background: '#fff', color: '#777', cursor: 'pointer' }}>
              지정 해제
            </button>
          )}
        </div>
      </div>
    );
  }
  const runSearch = async (kw) => {
    setKeyword(kw);
    if (!kw.trim() || !searchPlatform) { setCandidates([]); return; }
    setSearching(true);
    try {
      const r = await api.get('/categories/openmarket-mappings/search', { params: { platform: searchPlatform, keyword: kw.trim() } });
      setCandidates(r.data || []);
    } catch (err) { console.error(err); }
    finally { setSearching(false); }
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ fontSize: '10px', color: '#A32D2D', marginBottom: '3px', width: '180px', lineHeight: '1.4' }}>
        같은 대분류(가공식품 등) 안에서만 변경하세요. 대분류가 바뀌면 고시정보가 잘못 등록될 수 있습니다.
      </div>
      <input autoFocus value={keyword} onChange={e => runSearch(e.target.value)}
        placeholder="카테고리 키워드 검색"
        onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}
        style={{ fontSize: '12px', padding: '4px 6px', borderRadius: '4px', border: '0.5px solid #185FA5', width: '180px' }} />
      {(candidates.length > 0 || searching) && (
        <div style={{ position: 'absolute', top: '28px', left: 0, zIndex: 10, background: '#fff', border: '0.5px solid #ddd', borderRadius: '6px', width: '260px', maxHeight: '200px', overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {searching && <div style={{ padding: '8px', fontSize: '11px', color: '#aaa' }}>검색 중...</div>}
          {candidates.map((c, i) => (
            <div key={i} onClick={() => { onSave({ category_code: c.dispNo, category_name: c.dispNm }); setEditing(false); }}
              style={{ padding: '6px 10px', fontSize: '11px', cursor: 'pointer', borderBottom: '0.5px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              {c.dispNm}
            </div>
          ))}
        </div>
      )}
      <button onClick={() => setEditing(false)} style={{ fontSize: '10px', color: '#aaa', marginLeft: '4px', border: 'none', background: 'none', cursor: 'pointer' }}>취소</button>
    </div>
  );
}


// [기능 - 2026-09-07] 쿠팡 노출상품명 확인 팝업. 쿠팡은 우리가 등록한 상품명(sellerProductName)과
// 무관하게, 노출상품명(displayProductName)을 임의로 바꿀 수 있어(실전 확인됨), 전송완료 후
// 반드시 점검이 필요하다. 기간(전송완료시간 기준)을 선택해 조회 -> 불일치 항목만 직접 수정 ->
// "수정 적용" 한 번에 PUT수정+PUT승인요청까지 자동 처리한다.
function CoupangNameCheckModal({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(weekAgo);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const fetchCheck = async () => {
    setLoading(true);
    try {
      const r = await api.get('/products/coupang-name-check', { params: { dateFrom, dateTo } });
      setRows((r.data || []).map(row => ({ ...row, editedName: row.displayName })));
    } catch (err) {
      alert('조회 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    const toFix = rows.filter(r => !r.matched && !r.error && r.editedName !== r.displayName);
    if (toFix.length === 0) return alert('수정할 내용이 없습니다');
    if (!window.confirm(`${toFix.length}건의 노출상품명을 수정하시겠습니까?`)) return;
    setApplying(true);
    try {
      const res = await api.post('/products/coupang-name-fix', {
        items: toFix.map(r => ({ sellerProductId: r.sellerProductId, displayName: r.editedName })),
      });
      const ok = res.data.results.filter(r => r.success).length;
      alert(`${ok}건 수정 완료 (실패 ${res.data.results.length - ok}건)`);
      fetchCheck();
    } catch (err) {
      alert('수정 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', width: '680px', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>쿠팡 노출상품명 확인</div>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>전송완료 시간 기준으로 확인할 기간을 선택하세요.</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ fontSize: '12px', padding: '6px 8px', borderRadius: '8px', border: '0.5px solid #ddd' }} />
          <span style={{ color: '#aaa', fontSize: '12px' }}>~</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ fontSize: '12px', padding: '6px 8px', borderRadius: '8px', border: '0.5px solid #ddd' }} />
          <button onClick={fetchCheck} disabled={loading}
            style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '8px', border: '0.5px solid #7F77DD', background: loading ? '#f0f0f0' : '#EEEDFE', color: '#3C3489', cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? '확인 중...' : '확인'}
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', border: '0.5px solid #e8e8e8', borderRadius: '8px', minHeight: '160px' }}>
          {rows.length === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>기간을 선택하고 "확인"을 눌러주세요</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#fafafa', textAlign: 'left' }}>
                  <th style={{ padding: '8px 10px' }}>등록상품명(우리)</th>
                  <th style={{ padding: '8px 10px' }}>쿠팡 노출상품명 (수정 가능)</th>
                  <th style={{ padding: '8px 10px', width: '56px' }}>상태</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.sellerProductId} style={{ borderTop: '0.5px solid #f0f0f0', background: r.matched ? 'transparent' : '#FCEBEB' }}>
                    <td style={{ padding: '8px 10px' }}>{r.registeredName}</td>
                    <td style={{ padding: '8px 10px' }}>
                      {r.matched || r.error ? (
                        <span style={{ color: '#aaa' }}>{r.displayName}</span>
                      ) : (
                        <input value={r.editedName} onChange={e => setRows(prev => prev.map(x => x.sellerProductId === r.sellerProductId ? { ...x, editedName: e.target.value } : x))}
                          style={{ width: '100%', boxSizing: 'border-box', border: '0.5px solid #ddd', borderRadius: '6px', padding: '4px 6px', fontSize: '12px' }} />
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', color: r.error ? '#aaa' : r.matched ? '#1D9E75' : '#E24B4A', fontWeight: '500' }}>
                      {r.error ? '오류' : r.matched ? '일치' : '불일치'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
          <button onClick={onClose} style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>닫기</button>
          <button onClick={apply} disabled={applying || rows.length === 0}
            style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #7F77DD', background: applying ? '#f0f0f0' : '#EEEDFE', color: '#3C3489', cursor: applying ? 'not-allowed' : 'pointer' }}>
            {applying ? '적용 중...' : '수정 적용'}
          </button>
        </div>
      </div>
    </div>
  );
}


// [기능 추가 - 2026-09-09] 쿠팡 "전송실패" 탭 전용. 이 상품의 카테고리에서 필수인
// 구매옵션(개당중량/개당용량/수량처럼 자동으로 채우지 못하는 것들, 예: 색상/총수량)을
// 캐싱된 메타정보에서 동적으로 가져와 입력창을 만들고, 값을 저장한다.
// [기능 추가 - 2026-09-10] 토스쇼핑 필수 구매옵션 입력 모달. 쿠팡과 정확히 같은 패턴이되,
// 필드 키가 attributeTypeName이 아니라 key이고, unitValues(허용단위 목록)와
// valueComment(입력 안내문)가 있어 좀 더 자세한 안내를 보여준다.
function TossExtraAttributesModal({ product, onClose }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/products/${product.id}/toss-required-attributes`);
        setFields(r.data || []);
        const initial = {};
        (r.data || []).forEach(f => { initial[f.key] = ''; });
        setValues(initial);
      } catch (err) {
        alert('필수옵션 조회 실패: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [product.id]);

  const save = async () => {
    setSaving(true);
    try {
      // 빈 값은 제외하고 저장 - 택1 그룹 중 하나만 채우면 되므로 빈 값을 그대로 보내면 안 된다.
      const filled = Object.fromEntries(Object.entries(values).filter(([, v]) => v));
      await api.post(`/products/${product.id}/toss-extra-attributes`, { attributes: filled });
      alert('저장되었습니다. 재전송 버튼을 눌러 다시 시도해주세요.');
      onClose();
    } catch (err) {
      alert('저장 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', width: '440px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>필수 구매옵션 입력 (토스쇼핑)</div>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '14px' }}>{product.name}</div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>불러오는 중...</div>
          ) : fields.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>이 카테고리의 필수 구매옵션 정보를 찾을 수 없습니다. 오픈마켓 매칭을 먼저 확인해주세요.</div>
          ) : (
            fields.map(f => (
              <div key={f.key} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#555', marginBottom: '2px' }}>
                  {f.key} {f.isOneOfRequiredGroup ? <span style={{ color: '#EF9F27' }}>(택1)</span> : ''}
                </div>
                {f.valueComment && <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '4px', whiteSpace: 'pre-wrap' }}>{f.valueComment}</div>}
                <input value={values[f.key] || ''} onChange={e => setValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={f.placeholder || (f.unitValues && f.unitValues.length > 0 ? `예: 값${f.unitValues[0]}` : '값 입력')}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: '13px', padding: '7px 10px', borderRadius: '8px', border: '0.5px solid #ddd' }} />
              </div>
            ))
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
          <button onClick={onClose} style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>닫기</button>
          <button onClick={save} disabled={saving || fields.length === 0}
            style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #7F77DD', background: saving ? '#f0f0f0' : '#EEEDFE', color: '#3C3489', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CoupangExtraAttributesModal({ product, onClose }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/products/${product.id}/coupang-required-attributes`);
        setFields(r.data || []);
        const initial = {};
        (r.data || []).forEach(f => { initial[f.attributeTypeName] = ''; });
        setValues(initial);
      } catch (err) {
        alert('필수옵션 조회 실패: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [product.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/products/${product.id}/coupang-extra-attributes`, { attributes: values });
      alert('저장되었습니다. 재전송 버튼을 눌러 다시 시도해주세요.');
      onClose();
    } catch (err) {
      alert('저장 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', width: '440px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>필수 구매옵션 입력</div>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '14px' }}>{product.name}</div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>불러오는 중...</div>
          ) : fields.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>이 카테고리의 필수 구매옵션 정보를 찾을 수 없습니다. 오픈마켓 매칭을 먼저 확인해주세요.</div>
          ) : (
            fields.map(f => (
              <div key={f.attributeTypeName} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>
                  {f.attributeTypeName} {f.basicUnit && f.basicUnit !== '없음' ? `(${f.basicUnit})` : ''}
                </div>
                <input value={values[f.attributeTypeName] || ''} onChange={e => setValues(prev => ({ ...prev, [f.attributeTypeName]: e.target.value }))}
                  placeholder={f.usableUnits && f.usableUnits.length > 0 ? `예: 값 ${f.usableUnits[0]}` : '값 입력'}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: '13px', padding: '7px 10px', borderRadius: '8px', border: '0.5px solid #ddd' }} />
              </div>
            ))
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
          <button onClick={onClose} style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>닫기</button>
          <button onClick={save} disabled={saving || fields.length === 0}
            style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #7F77DD', background: saving ? '#f0f0f0' : '#EEEDFE', color: '#3C3489', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}


// [기능 추가 - 2026-09-09] ESM(지마켓/옥션) 브랜드 확정 모달. "미등록항목 자동채우기" 실행 전,
// 아직 확정 안 된 (대분류+브랜드명) 조합을 하나씩 사용자에게 보여준다. 오레오->몬델레즈,
// 동아(문구)->동아스포츠처럼 첫 번째 검색결과를 그냥 채택하면 안 되는 사고를 방지하기 위해,
// 반드시 사용자가 직접 정확한 후보를 선택해야 다음 단계로 넘어간다. 한번 확정하면 같은
// (대분류+브랜드명) 조합은 esm_brand_confirmations에 저장되어 다음부터 자동 재사용된다.
function EsmBrandConfirmModal({ items, onDone, onCancel }) {
  const [index, setIndex] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const PAGE_SIZE = 20;
  const current = items[index];
  // [기능 추가 - 2026-09-27] 상품에 저장된 브랜드명(예: 바리스타룰스)으로 검색하면 결과가
  // 0건이라 막다른 화면이 되는 문제가 실전에서 확인됐다. 사용자가 검색어를 직접 바꿔서
  // 재검색할 수 있게 한다. 재검색 결과에서 고르더라도 저장은 원래 상품의 브랜드명
  // (current.brandKeyword) 기준으로 해야 다음 전송 때 같은 조합이 자동 재사용된다.
  const [customKeyword, setCustomKeyword] = useState('');

  const runSearch = async (keyword, isCustom) => {
    setLoading(true);
    setPage(1);
    try {
      // 재검색(isCustom)일 때는 largeCategory를 넘기지 않는다 - 이미 확정된 조합이 있어도
      // 후보 목록을 그대로 받아야 하기 때문(넘기면 confirmed만 돌려주고 candidates가 빈다).
      const params = isCustom ? { keyword } : { keyword, largeCategory: current.largeCategory };
      const r = await api.get('/products/esm-brand-search', { params });
      if (!isCustom && r.data.confirmed) {
        // 이미 다른 항목 처리 중 같은 조합이 확정된 경우 - 바로 다음으로 넘어간다.
        setIndex(i => i + 1);
        return;
      }
      setCandidates(r.data.candidates || []);
    } catch (err) {
      alert('브랜드 검색 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!current) return;
    setCustomKeyword(current.brandKeyword);
    runSearch(current.brandKeyword, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    if (index >= items.length && items.length > 0) onDone();
  }, [index, items.length, onDone]);

  if (!current) return null;

  const select = async (cand) => {
    setSaving(true);
    try {
      await api.post('/products/esm-brand-confirm', {
        largeCategory: current.largeCategory, brandKeyword: current.brandKeyword,
        makerSeq: cand.makerSeq, brandSeq: cand.brandSeq, makerName: cand.makerName, brandName: cand.brandName,
        productBrandSeq: cand.productBrandSeq, productBrandName: cand.productBrandName,
      });
      setIndex(i => i + 1);
    } catch (err) {
      alert('저장 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(candidates.length / PAGE_SIZE));
  const pageCandidates = candidates.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', width: '480px', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>브랜드 확인 필요 ({index + 1}/{items.length})</div>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>대분류: {current.largeCategory}</div>
        <div style={{ fontSize: '13px', color: '#1a1a1a', marginBottom: '8px' }}>검색어: <b>{current.brandKeyword}</b> — 정확한 브랜드/제조사를 선택해주세요</div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
          <input type="text" value={customKeyword} placeholder="다른 검색어로 재검색 (예: 영문명, 띄어쓰기 변경)"
            onChange={e => setCustomKeyword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && customKeyword.trim() && !loading) runSearch(customKeyword.trim(), true); }}
            style={{ flex: 1, padding: '7px 10px', border: '0.5px solid #ddd', borderRadius: '8px', fontSize: '12px' }} />
          <button onClick={() => customKeyword.trim() && runSearch(customKeyword.trim(), true)} disabled={loading || !customKeyword.trim()}
            style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: loading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
            재검색
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', border: '0.5px solid #eee', borderRadius: '8px' }}>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>검색 중...</div>
          ) : pageCandidates.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>검색 결과가 없습니다</div>
          ) : (
            pageCandidates.map((c, i) => (
              <div key={i} onClick={() => !saving && select(c)}
                style={{ padding: '10px 14px', borderBottom: '0.5px solid #f0f0f0', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {/* [기능 추가 - 2026-09-27] 같은 제조사+브랜드 조합(예: 매일유업/매일유업)이라도
                    상품브랜드(productBrandName, 예: 상하목장/매일우유/아몬드브리즈)가 서로
                    달라 여러 후보로 나오는데, 기존에는 이 값이 안 보여서 전부 똑같아 보였다. */}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px' }}>{c.brandName}</span>
                  <span style={{ fontSize: '12px', color: '#999' }}>{c.makerName}</span>
                </div>
                <div style={{ fontSize: '12px', color: c.productBrandName ? '#185FA5' : '#ccc' }}>
                  상품브랜드: {c.productBrandName || '상품브랜드 없음'}
                </div>
              </div>
            ))
          )}
        </div>
        {candidates.length > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '8px' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>이전</button>
            <span style={{ fontSize: '12px', color: '#888', padding: '4px 6px' }}>{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>다음</button>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
          <button onClick={onCancel} style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>취소</button>
        </div>
      </div>
    </div>
  );
}

// [기능 추가 - 2026-10-14] 쿠팡 "전송실패" 탭 전용 브랜드 선택 모달.
// 쿠팡은 브랜드명만으로는 등록되지 않는 브랜드가 있어("브랜드 ID가 필요합니다"), 쿠팡에 등록된
// 정확한 브랜드를 검색해 고르면 products.coupang_brand_id/coupang_brand_name에 저장한다.
// (우리 상품의 원래 브랜드명(products.brand)은 바꾸지 않는다.) 저장 후 "재전송"을 누르면 된다.
// 서버: GET /products/coupang-brand-search?q=, POST /products/coupang-brand
function CoupangBrandModal({ product, onClose }) {
  const [keyword, setKeyword] = useState(product.brand || '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [saving, setSaving] = useState(false);

  const runSearch = async (kw) => {
    setLoading(true);
    try {
      const r = await api.get('/products/coupang-brand-search', { params: { q: kw } });
      setItems(Array.isArray(r.data) ? r.data : []);
      setSearched(true);
    } catch (err) {
      alert('브랜드 검색 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keyword.trim()) runSearch(keyword.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = async (item) => {
    if (!window.confirm(`쿠팡 브랜드를 "${item.brandName}" (${item.brandId})로 저장하시겠습니까?`)) return;
    setSaving(true);
    try {
      await api.post('/products/coupang-brand', { productId: product.id, brandId: item.brandId, brandName: item.brandName });
      alert('저장되었습니다. 재전송 버튼을 눌러 다시 시도해주세요.');
      onClose();
    } catch (err) {
      alert('저장 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', width: '460px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>쿠팡 브랜드 선택</div>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>{product.name}</div>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>우리 상품 브랜드: <b>{product.brand || '-'}</b> — 쿠팡에 등록된 정확한 브랜드를 선택해주세요</div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
          <input type="text" value={keyword} placeholder="브랜드명 검색 (예: 서울대밥스누)"
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && keyword.trim() && !loading) runSearch(keyword.trim()); }}
            style={{ flex: 1, padding: '7px 10px', border: '0.5px solid #ddd', borderRadius: '8px', fontSize: '12px' }} />
          <button onClick={() => keyword.trim() && runSearch(keyword.trim())} disabled={loading || !keyword.trim()}
            style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: loading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
            검색
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', border: '0.5px solid #eee', borderRadius: '8px', minHeight: '80px' }}>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>검색 중...</div>
          ) : !searched ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>브랜드명을 입력하고 검색해주세요</div>
          ) : items.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>검색 결과가 없습니다. 검색어를 바꿔보거나, 쿠팡 WING 브랜드 관리에서 먼저 브랜드를 등록해주세요.</div>
          ) : (
            items.map(it => (
              <div key={it.brandId} onClick={() => !saving && select(it)}
                style={{ padding: '10px 14px', borderBottom: '0.5px solid #f0f0f0', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px' }}>{it.brandName}</span>
                <span style={{ fontSize: '12px', color: '#999' }}>{it.brandId}</span>
              </div>
            ))
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
          <button onClick={onClose} style={{ fontSize: '12px', padding: '7px 14px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>닫기</button>
        </div>
      </div>
    </div>
  );
}

export default function ProductSend() {
  const [platform, setPlatform] = useState('SMARTSTORE');
  const [tab, setTab] = useState('unsent');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [showCoupangNameCheck, setShowCoupangNameCheck] = useState(false);
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageInput, setPageInput] = useState(''); // 페이지 직접 이동 입력값
  const [pageLimit, setPageLimit] = useState(20); // [기능 추가 - 2026-10-26] 한 페이지에 보여줄 개수 (20/50/60/80/100)
  // [설계 원칙] 판매처 전송용 오버라이드(상품명/카테고리/가격/재고). 우리 상품 기본정보는 절대 안 건드리고,
  // "미전송" 탭에서 이 값이 있으면 전송 시 우선 사용, 없으면 원래 로직 그대로 쓴다. 스마트스토어는 사용 안 함.
  const [sendConfigs, setSendConfigs] = useState({}); // { [productId]: {name, category_code, category_name, price, stock} }
  const [matchedCategories, setMatchedCategories] = useState({}); // { [productId]: 이 판매처에 매칭된 카테고리 이름 }
  const [editingCell, setEditingCell] = useState(null); // { productId, field }
  const [savingCell, setSavingCell] = useState(false);
  // ESM 확인파일(등록된 상품 목록 엑셀) 업로드 진행상황 - jobId로 폴링하며 results 배열이 한 건씩 늘어남
  const [confirmJobId, setConfirmJobId] = useState(null);
  const [confirmJob, setConfirmJob] = useState(null);
  const [confirmUploading, setConfirmUploading] = useState(false);
  // ESM 상품 상세 자동보정(판매자관리코드/브랜드/제조사/외부제휴채널) - 확인파일 업로드로 상품번호가
  // 확보된 상품들을 대상으로 GET->PUT을 실행해, 성공해야 진짜 '전송완료'로 전환한다.
  const [fixJobId, setFixJobId] = useState(null);
  const [fixJob, setFixJob] = useState(null);
  const [fixRunning, setFixRunning] = useState(false);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [largeCat, setLargeCat] = useState('');
  const [middleCat, setMiddleCat] = useState('');
  const [middleCats, setMiddleCats] = useState([]);
  const [retryingId, setRetryingId] = useState(null);
  const [extraAttrProduct, setExtraAttrProduct] = useState(null);
  const [brandProduct, setBrandProduct] = useState(null); // 쿠팡 브랜드 선택 모달 대상 상품

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/categories/large', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!largeCat) { setMiddleCats([]); setMiddleCat(''); return; }
    const token = localStorage.getItem('token');
    axios.get(`/api/categories/middle?large=${encodeURIComponent(largeCat)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMiddleCats(r.data || [])).catch(() => {});
  }, [largeCat]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { platform, platformStatus: tab, page, limit: pageLimit };
      if (search) params.search = search;
      if (largeCat) params.largeCat = largeCat;
      if (middleCat) params.middleCat = middleCat;
      const r = await api.get('/products', { params });
      setProducts(r.data.products || []);
      setTotalPages(r.data.totalPages || 1);
      setTotal(r.data.total || 0);
      if (platform !== 'SMARTSTORE') {
        const cfgRes = await api.get('/products/platform-send-config', { params: { platform } });
        setSendConfigs(cfgRes.data || {});
        try {
          const mRes = await api.get('/products/platform-matched-categories', { params: { platform } });
          setMatchedCategories(mRes.data || {});
        } catch (e) { setMatchedCategories({}); }
      } else {
        setSendConfigs({});
        setMatchedCategories({});
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [platform, tab, page, search, largeCat, middleCat, pageLimit]);

  // [기능 추가 - 2026-09-13] "전송 제외": 택배비 있는 상품, 쿠팡전용 상품 등 특정 판매처에
  // 전송할 계획이 없는 상품을, 미전송 탭에 실수로 남겨두지 않고 별도 상태로 분리 관리한다.
  const handleExclude = async (productId) => {
    if (!window.confirm('이 상품을 전송제외로 지정하시겠습니까?')) return;
    try {
      await api.post(`/products/${productId}/exclude-platform`, { platform });
      fetchProducts();
    } catch (err) {
      alert('처리 실패: ' + (err.response?.data?.message || err.message));
    }
  };
  // [기능 추가 - 2026-09-13] 미전송 탭에서 여러 상품을 체크박스로 선택해 한 번에 전송제외
  // 처리한다(사용자 확인: "선택해서" 전송제외로 보내는 게 원래 의도).
  const handleExcludeBulk = async () => {
    if (selectedIds.length === 0) return alert('상품을 선택해주세요');
    if (!window.confirm(`선택한 ${selectedIds.length}건을 전송제외로 지정하시겠습니까?`)) return;
    try {
      await Promise.all(selectedIds.map(id => api.post(`/products/${id}/exclude-platform`, { platform })));
      setSelectedIds([]);
      fetchProducts();
    } catch (err) {
      alert('처리 실패: ' + (err.response?.data?.message || err.message));
    }
  };
  const handleUnexclude = async (productId) => {
    try {
      await api.post(`/products/${productId}/unexclude-platform`, { platform });
      fetchProducts();
    } catch (err) {
      alert('처리 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  useEffect(() => { fetchProducts(); setSelectedIds([]); setResults([]); }, [fetchProducts]);
  useEffect(() => { setPage(1); }, [tab, search, largeCat, middleCat]);

  // ESM 확인파일 업로드 -> 작업 시작, jobId 확보되면 1초 간격으로 폴링해서 results를 화면에 반영
  // [설계 원칙 - 2026-09-04] ESM/셀러허브 모두 "확인파일 업로드" UI를 공유하지만, 백엔드 엔드포인트와
  // 파싱 로직은 서로 다르다(ESM=상품명 매칭, 셀러허브=상품코드 매칭). platform으로 분기한다.
  const confirmUploadEndpoint = platform === 'SELLERHUB' ? 'sellerhub-confirm-upload' : 'esm-confirm-upload';
  const handleConfirmFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setConfirmUploading(true);
    setConfirmJob(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/products/${confirmUploadEndpoint}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setConfirmJobId(res.data.jobId);
    } catch (err) {
      alert('업로드 실패: ' + (err.response?.data?.message || err.message));
      setConfirmUploading(false);
    }
  };
  useEffect(() => {
    if (!confirmJobId) return;
    const timer = setInterval(async () => {
      try {
        const res = await api.get(`/products/${confirmUploadEndpoint}/${confirmJobId}`);
        setConfirmJob(res.data);
        if (res.data.status === 'done' || res.data.status === 'error') {
          clearInterval(timer);
          setConfirmUploading(false);
          fetchProducts();
        }
      } catch (err) { console.error(err); }
    }, 1000);
    return () => clearInterval(timer);
  }, [confirmJobId, fetchProducts, confirmUploadEndpoint]);
  // 셀러허브 전용 - 고시정보 엑셀 다운로드 (전송중 탭에서 체크한 상품들 대상)
  const handleSellerhubNoticeDownload = async () => {
    if (selectedIds.length === 0) return alert('상품을 선택해주세요');
    setSending(true);
    try {
      const res = await api.post('/products/sellerhub-notice-excel', { productIds: selectedIds }, { responseType: 'blob' });
      const contentType = res.headers['content-type'] || '';
      const isZip = contentType.includes('zip');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', isZip ? '셀러허브_고시정보.zip' : '셀러허브_고시정보.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('고시정보 엑셀 생성 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };
  // 셀러허브 전용 - 고시정보 등록을 셀러허브에서 직접 확인한 뒤, 체크한 상품들을 전송완료로 전환
  const handleSellerhubComplete = async () => {
    if (selectedIds.length === 0) return alert('상품을 선택해주세요');
    if (!window.confirm(`선택한 ${selectedIds.length}건을 전송완료로 처리하시겠습니까?\n셀러허브에서 고시정보 등록이 완료된 것을 먼저 확인해주세요.`)) return;
    setSending(true);
    try {
      const res = await api.post('/products/sellerhub-notice-complete', { productIds: selectedIds });
      alert(res.data.message);
      setSelectedIds([]);
      fetchProducts();
    } catch (err) {
      alert('완료 처리 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };
  const [unconfirmedBrands, setUnconfirmedBrands] = useState(null);
  const runFieldFix = async () => {
    setFixRunning(true);
    setFixJob(null);
    try {
      const res = await api.post('/products/esm-field-fix', { platform });
      setFixJobId(res.data.jobId);
    } catch (err) {
      alert('시작 실패: ' + (err.response?.data?.message || err.message));
      setFixRunning(false);
    }
  };
  const handleFieldFix = async () => {
    if (!window.confirm('상품번호가 확보된 상품들의 판매자관리코드/브랜드/제조사/외부제휴채널을 자동으로 채워넣으시겠습니까?\n성공한 건만 전송완료로 전환됩니다.')) return;
    try {
      // [기능 추가 - 2026-09-09] 실행 전, 아직 확정 안 된 브랜드가 있으면 먼저 확인부터 받는다
      // (첫 번째 검색결과를 그냥 채택하다 오레오->몬델레즈, 동아(문구)->동아스포츠로 잘못
      // 매칭된 사고를 방지하기 위함 - ESM은 등록 후 브랜드 수정이 불가능하다).
      const r = await api.get('/products/esm-field-fix/unconfirmed-brands', { params: { platform } });
      if ((r.data || []).length > 0) {
        setUnconfirmedBrands(r.data);
        return;
      }
    } catch (err) {
      alert('사전 점검 실패: ' + (err.response?.data?.message || err.message));
      return;
    }
    runFieldFix();
  };
  useEffect(() => {
    if (!fixJobId) return;
    const timer = setInterval(async () => {
      try {
        const res = await api.get(`/products/esm-confirm-upload/${fixJobId}`);
        setFixJob(res.data);
        if (res.data.status === 'done' || res.data.status === 'error') {
          clearInterval(timer);
          setFixRunning(false);
          fetchProducts();
        }
      } catch (err) { console.error(err); }
    }, 1000);
    return () => clearInterval(timer);
  }, [fixJobId, fetchProducts]);
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  // 전송용 오버라이드 저장 (우리 상품 기본정보 products는 절대 건드리지 않음, product_platform_send_config에만 저장)
  const saveSendConfig = async (productId, field, value) => {
    return saveSendConfigMulti(productId, { [field]: value });
  };
  // 카테고리처럼 여러 필드(code+name)를 한 번에 저장해야 할 때, 순차 호출로 인한 경쟁상태(뒤 호출이
  // 앞 호출 결과를 못 보고 덮어쓰는 문제)를 막기 위해 하나의 요청으로 합쳐 보낸다.
  const saveSendConfigMulti = async (productId, fields) => {
    setSavingCell(true);
    try {
      const existing = sendConfigs[productId] || {};
      const payload = {
        platform,
        name: existing.name ?? null,
        category_code: existing.category_code ?? null,
        category_name: existing.category_name ?? null,
        price: existing.price ?? null,
        stock: existing.stock ?? null,
        ...fields,
      };
      await api.post(`/products/${productId}/platform-send-config`, payload);
      setSendConfigs(prev => ({ ...prev, [productId]: { ...existing, ...fields } }));
      setEditingCell(null);
    } catch (err) {
      alert('저장 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingCell(false);
    }
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === products.length) setSelectedIds([]);
    else setSelectedIds(products.map(p => p.id));
  };

  // [설계 원칙 - 2026-09-04] ESM(지마켓/옥션)과 셀러허브는 둘 다 "엑셀 다운로드" 방식이지만
  // 서로 다른 백엔드 엔드포인트/생성 로직을 쓴다. 이 함수는 UI(버튼/파일명)만 공통으로 처리하고,
  // 실제 엑셀 생성 요청은 platform에 따라 분기한다. 향후 다른 엑셀 판매처 추가 시 이 분기만 늘리면 된다.
  const handleExcelDownload = async () => {
    const platformLabel = platform === 'GMARKET' ? '지마켓' : platform === 'AUCTION' ? '옥션' : platform === 'SELLERHUB' ? '셀러허브' : 'esm';
    if (!window.confirm(`선택한 ${selectedIds.length}건의 상품등록 엑셀을 다운로드하시겠습니까?\n다운로드 후 ${platformLabel} 사이트(상품 일괄등록)에 직접 업로드해주세요.`)) return;
    setSending(true);
    try {
      let res;
      if (platform === 'SELLERHUB') {
        res = await api.post('/products/sellerhub-bulk-excel', { productIds: sortSelectedIdsByProductCode(selectedIds) }, { responseType: 'blob' });
      } else {
        const targetSites = platform === 'GMARKET' ? ['gmarket'] : platform === 'AUCTION' ? ['auction'] : ['auction', 'gmarket'];
        res = await api.post('/products/esm-bulk-excel', { productIds: sortSelectedIdsByProductCode(selectedIds), targetSites, platform }, { responseType: 'blob' });
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}`;
      link.setAttribute('download', `${platformLabel}_상품등록_${dateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSelectedIds([]);
      fetchProducts();
    } catch (err) {
      alert('엑셀 생성 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };
  // [설계 원칙 - 2026-09-06] 쿠팡도 스마트스토어/11번가와 똑같이 "선택 상품 전송" 버튼을
  // 그대로 재사용한다. 다만 등록 API 호출을 개별 상품 하나하나 프론트에서 순차 호출하지 않고,
  // 백엔드(coupang-send)에서 배열로 한 번에 처리한다 - 승인 절차가 있어 상태 흐름이 다르기 때문.
  const handleCoupangSend = async () => {
    if (!window.confirm(`선택한 ${selectedIds.length}건을 쿠팡에 전송하시겠습니까?`)) return;
    setSending(true);
    try {
      const res = await api.post('/products/coupang-send', { productIds: sortSelectedIdsByProductCode(selectedIds) });
      setResults(res.data.results.map(r => ({ id: r.id, name: products.find(p => p.id === r.id)?.name, success: r.success, message: r.message })));
      setSelectedIds([]);
      fetchProducts();
    } catch (err) {
      alert('전송 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };
  // 개별 상품의 쿠팡 승인상태를 확인하고, 승인완료면 vendorItemId를 확보한다.
  const handleCoupangCheckStatus = async (productId) => {
    try {
      const res = await api.post(`/products/coupang-check-status/${productId}`);
      if (res.data.ready) {
        alert('승인완료되었습니다. 쿠팡 Wing에서 자동생성옵션이 비활성화되었는지 확인 후 완료 처리해주세요.');
        fetchProducts();
      } else if (res.data.failed) {
        alert(`승인반려되어 전송실패로 이동되었습니다.\n사유: ${res.data.reason}`);
        fetchProducts();
      } else {
        alert(`현재 상태: ${res.data.statusName}`);
      }
    } catch (err) {
      alert('확인 실패: ' + (err.response?.data?.message || err.message));
    }
  };
  // Wing에서 자동생성옵션 비활성화를 직접 확인한 뒤, 체크한 상품들을 전송완료로 전환
  const handleCoupangComplete = async () => {
    if (selectedIds.length === 0) return alert('상품을 선택해주세요');
    if (!window.confirm(`선택한 ${selectedIds.length}건을 전송완료로 처리하시겠습니까?\n쿠팡 Wing에서 자동생성옵션 비활성화를 먼저 확인해주세요.`)) return;
    setSending(true);
    try {
      const res = await api.post('/products/coupang-complete', { productIds: selectedIds });
      alert(res.data.message);
      setSelectedIds([]);
      fetchProducts();
    } catch (err) {
      alert('완료 처리 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };
  // [기능 추가 - 2026-09-10] 토스쇼핑도 쿠팡과 정확히 같은 방식: 검수(AI 자동) 절차가
  // 있어 상태 흐름이 다르므로, 백엔드(toss-send)에서 배열로 한 번에 처리한다.
  const handleTossSend = async () => {
    if (!window.confirm(`선택한 ${selectedIds.length}건을 토스쇼핑에 전송하시겠습니까?`)) return;
    setSending(true);
    try {
      const res = await api.post('/products/toss-send', { productIds: sortSelectedIdsByProductCode(selectedIds) });
      setResults(res.data.results.map(r => ({ id: r.id, name: products.find(p => p.id === r.id)?.name, success: r.success, message: r.message })));
      setSelectedIds([]);
      fetchProducts();
    } catch (err) {
      alert('전송 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };
  // 개별 상품의 토스쇼핑 검수상태를 확인하고, 완료/반려에 따라 자동으로 상태를 전환한다.
  const handleTossCheckStatus = async (productId) => {
    try {
      const res = await api.post(`/products/toss-check-status/${productId}`);
      if (res.data.ready) {
        alert('검수가 완료되어 전송완료로 전환됐습니다.');
        fetchProducts();
      } else if (res.data.failed) {
        alert(`검수 반려되어 전송실패로 이동되었습니다.\n사유: ${res.data.reason}`);
        fetchProducts();
      } else {
        alert('아직 검수 진행 중입니다.');
      }
    } catch (err) {
      alert('확인 실패: ' + (err.response?.data?.message || err.message));
    }
  };
  // [기능 추가 - 2026-09-21] 화면 목록은 등록일 최신순(과거 상품을 골라 전송하기
  // 위해 그대로 유지)이지만, 실제 전송은 상품코드 순서로 진행되길 원해서, 전송 직전에만
  // selectedIds를 상품코드 기준으로 정렬한다. 체크박스 표시 순서(products 배열 순서)에는
  // 영향 없음 - 오직 "어떤 순서로 API를 호출하는지"만 바뀐다.
  const sortSelectedIdsByProductCode = (ids) => {
    return [...ids].sort((a, b) => {
      const codeA = products.find(p => p.id === a)?.product_code || '';
      const codeB = products.find(p => p.id === b)?.product_code || '';
      return codeA.localeCompare(codeB);
    });
  };

  const handleSend = async () => {
    if (selectedIds.length === 0) return alert('전송할 상품을 선택하세요');
    if (EXCEL_PLATFORM_CODES.includes(platform)) return handleExcelDownload();
    if (platform === 'COUPANG') return handleCoupangSend();
    if (platform === 'TOSS') return handleTossSend();
    if (!window.confirm(`선택한 ${selectedIds.length}건을 ${PLATFORMS.find(p => p.code === platform).label}에 전송하시겠습니까?`)) return;
    setSending(true);
    setResults([]);
    const newResults = [];
    for (const id of sortSelectedIdsByProductCode(selectedIds)) {
      const product = products.find(p => p.id === id);
      try {
        const r = await api.post(`/products/${id}/${SEND_ENDPOINT[platform]}`);
        newResults.push({ id, name: product?.name, code: product?.product_code, success: true, message: r.data.message });
      } catch (err) {
        newResults.push({ id, name: product?.name, code: product?.product_code, success: false, message: err.response?.data?.message || err.message });
      }
      setResults([...newResults]);
    }
    setSending(false);
    setSelectedIds([]);
  };

  const closeResults = () => {
    setResults([]);
    fetchProducts();
  };

  const handleRetry = async (product) => {
    setRetryingId(product.id);
    try {
      if (platform === 'COUPANG') {
        // 쿠팡은 다른 판매처와 엔드포인트 패턴이 달라(배열로 여러 상품을 한번에 받는 방식),
        // 재전송도 정확히 coupang-send를 그대로 재사용한다(단건이라도 배열로 감싸서 호출).
        const res = await api.post('/products/coupang-send', { productIds: [product.id] });
        const r = res.data.results?.[0];
        if (r && !r.success) throw new Error(r.message);
      } else if (platform === 'TOSS') {
        // 토스쇼핑도 정확히 쿠팡과 같은 패턴(배열로 여러 상품 한번에 받는 엔드포인트).
        const res = await api.post('/products/toss-send', { productIds: [product.id] });
        const r = res.data.results?.[0];
        if (r && !r.success) throw new Error(r.message);
      } else {
        await api.post(`/products/${product.id}/${SEND_ENDPOINT[platform]}`);
      }
      fetchProducts();
    } catch (err) {
      alert('재전송 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setRetryingId(null);
    }
  };

  const th = { padding: '8px 14px', textAlign: 'left', fontSize: '11px', color: '#888', fontWeight: '500' };
  const td = { padding: '10px 14px', fontSize: '12px', color: '#1a1a1a' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>상품전송</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <select value={platform} onChange={e => { setPlatform(e.target.value); setPage(1); }}
            style={{ fontSize: '13px', padding: '7px 12px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff' }}>
            {PLATFORMS.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
          </select>
          {tab === 'unsent' && (
            <button onClick={handleSend} disabled={sending || selectedIds.length === 0}
              style={{ fontSize: '13px', padding: '10px 24px', borderRadius: '8px', border: 'none', background: sending || selectedIds.length === 0 ? '#ccc' : '#1D9E75', color: '#fff', cursor: sending || selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
              {sending ? (EXCEL_PLATFORM_CODES.includes(platform) ? '엑셀 생성 중...' : `전송 중... (${results.length}/${selectedIds.length})`) : (EXCEL_PLATFORM_CODES.includes(platform) ? `엑셀 다운로드 (${selectedIds.length}건)` : `선택 상품 전송 (${selectedIds.length}건)`)}
            </button>
          )}
          {tab === 'unsent' && (
            <button onClick={handleExcludeBulk} disabled={sending || selectedIds.length === 0}
              style={{ fontSize: '13px', padding: '10px 24px', borderRadius: '8px', border: '0.5px solid #ccc', background: sending || selectedIds.length === 0 ? '#f5f5f5' : '#fff', color: sending || selectedIds.length === 0 ? '#ccc' : '#777', cursor: sending || selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
              선택 상품 전송제외 ({selectedIds.length}건)
            </button>
          )}
        </div>
      </div>
      {ESM_PLATFORM_CODES.includes(platform) && (
        <div style={{ fontSize: '11px', color: '#854F0B', background: '#FAEEDA', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', lineHeight: '1.6' }}>
          판매자관리코드/브랜드/제조사/외부제휴채널 상품등록은 엑셀 업로드로 설정할 수 없습니다.<br/>
          "확인파일 업로드"로 상품번호를 확인한 뒤, "미등록항목 자동채우기"를 실행해주세요.
        </div>
      )}
      {platform === 'SELLERHUB' && (
        <div style={{ fontSize: '11px', color: '#854F0B', background: '#FAEEDA', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', lineHeight: '1.6' }}>
          고시정보(원산지/제조사 등)는 엑셀 업로드로 설정할 수 없습니다.<br/>
          "확인파일 업로드"로 상품번호를 확인한 뒤, "전송중" 탭에서 고시정보 엑셀을 다운로드해 셀러허브에 별도 업로드해주세요.
        </div>
      )}

      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px 12px 0 0', display: 'flex' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ flex: 'none', padding: '12px 20px', fontSize: '13px', fontWeight: tab === t.key ? '600' : '400',
              // [기능 추가 - 2026-09-13] "전송제외"는 다른 4개 상태(정상 흐름)와 성격이 달라
              // (사용자가 의도적으로 보류시킨 상품) 회색 계열로 시각적으로 구분한다.
              color: tab === t.key ? (t.key === 'failed' ? '#E24B4A' : t.key === 'excluded' ? '#888' : '#1D9E75') : '#888',
              background: 'transparent', border: 'none', borderBottom: tab === t.key ? `2px solid ${t.key === 'failed' ? '#E24B4A' : t.key === 'excluded' ? '#888' : '#1D9E75'}` : '2px solid transparent', cursor: 'pointer' }}>
            {t.label} {tab === t.key ? `(${total})` : ''}
          </button>
        ))}
      </div>

      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderTop: 'none', padding: '12px 14px', display: 'flex', gap: '8px' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="상품명 또는 상품코드 검색"
          style={{ flex: 1, fontSize: '12px', padding: '6px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8' }} />
        <select value={largeCat} onChange={e => setLargeCat(e.target.value)} style={{ fontSize: '12px', padding: '6px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff' }}>
          <option value="">전체 대분류</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={middleCat} onChange={e => setMiddleCat(e.target.value)} disabled={!largeCat} style={{ fontSize: '12px', padding: '6px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff' }}>
          <option value="">전체 중분류</option>
          {middleCats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {platform === 'COUPANG' && tab === 'sent' && (
        <div style={{ fontSize: '11px', color: '#854F0B', background: '#FAEEDA', padding: '10px 14px', lineHeight: '1.6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>쿠팡은 노출상품명을 임의로 변경할 수 있습니다. 전송완료 후 반드시 "노출상품명 확인"으로 점검해주세요.</span>
          <button onClick={() => setShowCoupangNameCheck(true)}
            style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '8px', border: '0.5px solid #7F77DD', background: '#EEEDFE', color: '#3C3489', cursor: 'pointer', fontWeight: '500', flexShrink: 0, marginLeft: '10px' }}>
            노출상품명 확인
          </button>
        </div>
      )}

      {results.length > 0 && (
        <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', padding: '14px', marginTop: '0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500' }}>
              전송 결과 — 성공 {results.filter(r => r.success).length}건 / 실패 {results.filter(r => !r.success).length}건
            </div>
            <button onClick={closeResults} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer', color: '#666' }}>닫기</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '260px', overflowY: 'auto' }}>
            {results.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '6px', background: r.success ? '#F0FAF6' : '#FCEBEB' }}>
                <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '5px', background: r.success ? '#1D9E75' : '#E24B4A', color: '#fff', fontWeight: '500', flexShrink: 0 }}>
                  {r.success ? '성공' : '실패'}
                </span>
                <span style={{ fontSize: '11px', color: '#888', flexShrink: 0 }}>{r.code}</span>
                <span style={{ fontSize: '12px', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                {!r.success && <span style={{ fontSize: '11px', color: '#A32D2D', marginLeft: 'auto', flexShrink: 0, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.message}>{r.message}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
        {tab === 'unsent' && (
          <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '0.5px solid #e8e8e8', background: '#fafafa' }}>
            <input type="checkbox" checked={products.length > 0 && selectedIds.length === products.length} onChange={toggleSelectAll} style={{ marginRight: '10px' }} />
            <span style={{ fontSize: '12px', color: '#666' }}>전체 선택 ({selectedIds.length}/{products.length})</span>
          </div>
        )}

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>불러오는 중...</div>
        ) : products.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>
            {tab === 'unsent' ? '전송할 상품이 없습니다' : tab === 'pending' ? '전송중인 상품이 없습니다' : tab === 'sent' ? '전송완료된 상품이 없습니다' : tab === 'excluded' ? '전송제외된 상품이 없습니다' : '전송실패한 상품이 없습니다'}
          </div>
        ) : tab === 'unsent' ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#fafafa', borderBottom: '0.5px solid #e8e8e8' }}>
              <th style={th}><input type="checkbox" checked={selectedIds.length === products.length && products.length > 0} onChange={toggleSelectAll} disabled={sending} /></th>
              <th style={th}>등록일</th><th style={th}>상품코드</th><th style={th}></th><th style={th}>상품명</th><th style={th}>카테고리</th><th style={th}>가격</th><th style={th}>재고</th><th style={{ ...th, textAlign: 'right' }}>작업</th>
            </tr></thead>
            <tbody>
              {products.map(p => {
                const cfg = sendConfigs[p.id] || {};
                const canOverride = platform !== 'SMARTSTORE';
                const editCell = (field, displayValue, inputType = 'text') => {
                  const isEditing = editingCell && editingCell.productId === p.id && editingCell.field === field;
                  if (!canOverride) return <span>{displayValue}</span>;
                  if (isEditing) {
                    return (
                      <input
                        type={inputType}
                        autoFocus
                        defaultValue={cfg[field] ?? ''}
                        placeholder={String(displayValue)}
                        disabled={savingCell}
                        onBlur={e => {
                          const v = e.target.value.trim();
                          const parsed = inputType === 'number' ? (v === '' ? null : parseInt(v)) : (v === '' ? null : v);
                          saveSendConfig(p.id, field, parsed);
                        }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingCell(null); }}
                        style={{ fontSize: '12px', padding: '4px 6px', borderRadius: '4px', border: '0.5px solid #185FA5', width: inputType === 'number' ? '80px' : '160px' }}
                      />
                    );
                  }
                  const overridden = cfg[field] != null && cfg[field] !== '';
                  return (
                    <span onClick={() => setEditingCell({ productId: p.id, field })}
                      style={{ cursor: 'pointer', borderBottom: '1px dashed #ccc', color: overridden ? '#185FA5' : '#1a1a1a', fontWeight: overridden ? '600' : '400' }}
                      title="클릭하여 이 판매처 전송 시에만 적용될 값을 지정 (원본 상품정보는 변경되지 않음)">
                      {overridden ? cfg[field] : displayValue}
                    </span>
                  );
                };
                return (
                  <tr key={p.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                    <td style={td}><input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSelect(p.id)} disabled={sending} /></td>
                    <td style={td}>{formatDate(p.created_at)}</td>
                    <td style={{ ...td, color: '#888' }}>{p.product_code}</td>
                    <td style={td}>
                      <div style={{ width: '42px', height: '42px', borderRadius: '6px', overflow: 'hidden', background: '#f5f5f5' }}>
                        {p.thumbnail && <img src={`https://admin.jinhalife.co.kr${p.thumbnail}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                    </td>
                    <td style={td}>
                      {editCell('name', p.name)}
                      {platform === 'SELLERHUB' && p.brand && !p.sellerhub_brand_matched && (
                        <div style={{ fontSize: '10px', color: '#A32D2D', marginTop: '2px' }} title="설정 > 셀러허브 브랜드 매칭에서 브랜드 또는 별칭을 추가해주세요">
                          ⚠ 브랜드({p.brand}) 미매칭
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, color: '#888' }}>
                      <CategoryOverrideCell product={p} platform={platform} cfg={cfg} canOverride={canOverride} api={api}
                        matchedName={matchedCategories[p.id]}
                        onSave={(fields) => saveSendConfigMulti(p.id, fields)} />
                    </td>
                    <td style={td}>{editCell('price', p.expected_price, 'number')}</td>
                    <td style={td}>{editCell('stock', p.stock, 'number')}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => handleExclude(p.id)}
                        style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #ccc', background: '#f5f5f5', color: '#777', cursor: 'pointer' }}>
                        전송 제외
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : tab === 'excluded' ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#fafafa', borderBottom: '0.5px solid #e8e8e8' }}>
              <th style={th}></th><th style={th}>상품코드</th><th style={th}>상품명</th><th style={th}>카테고리</th><th style={{ ...th, textAlign: 'right' }}>작업</th>
            </tr></thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                  <td style={td}>
                    <div style={{ width: '42px', height: '42px', borderRadius: '6px', overflow: 'hidden', background: '#f5f5f5' }}>
                      {p.thumbnail && <img src={`https://admin.jinhalife.co.kr${p.thumbnail}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }} />}
                    </div>
                  </td>
                  <td style={{ ...td, color: '#aaa' }}>{p.product_code}</td>
                  <td style={{ ...td, color: '#aaa' }}>{p.name}</td>
                  <td style={{ ...td, color: '#aaa' }}><CategoryOverrideCell product={p} platform={platform} cfg={sendConfigs[p.id] || {}} canOverride={false} api={api} matchedName={matchedCategories[p.id]} /></td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => handleUnexclude(p.id)}
                      style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
                      미전송으로 복귀
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === 'sent' ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#fafafa', borderBottom: '0.5px solid #e8e8e8' }}>
              <th style={th}></th><th style={th}>상품명</th><th style={th}>전송완료시간</th><th style={th}>카테고리</th><th style={th}>상태</th>
            </tr></thead>
            <tbody>
              {products.map(p => {
                const status = (p.platform_send_status || []).find(s => s.platform === platform);
                return (
                  <tr key={p.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                    <td style={td}>
                      <div style={{ width: '42px', height: '42px', borderRadius: '6px', overflow: 'hidden', background: '#f5f5f5' }}>
                        {p.thumbnail && <img src={`https://admin.jinhalife.co.kr${p.thumbnail}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                    </td>
                    <td style={td}>{p.name}<div style={{ fontSize: '10px', color: '#aaa' }}>{p.product_code}</div></td>
                    <td style={td}>{formatDate(status?.sent_at)}</td>
                    <td style={{ ...td, color: '#888' }}><CategoryOverrideCell product={p} platform={platform} cfg={sendConfigs[p.id] || {}} canOverride={false} api={api} matchedName={matchedCategories[p.id]} /></td>
                    <td style={td}><span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#E1F5EE', color: '#085041' }}>완료</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : tab === 'pending' ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#fafafa', borderBottom: '0.5px solid #e8e8e8' }}>
              {(platform === 'SELLERHUB' || platform === 'COUPANG' || platform === 'TOSS') && <th style={th}><input type="checkbox" checked={selectedIds.length === products.length && products.length > 0} onChange={toggleSelectAll} /></th>}
              <th style={th}></th><th style={th}>상품명</th><th style={th}>{EXCEL_PLATFORM_CODES.includes(platform) ? '다운로드시간' : '전송시간'}</th><th style={th}>카테고리</th><th style={th}>상태</th>
              {(platform === 'COUPANG' || platform === 'TOSS') && <th style={{ ...th, textAlign: 'right' }}>작업</th>}
            </tr></thead>
            <tbody>
              {products.map(p => {
                const status = (p.platform_send_status || []).find(s => s.platform === platform);
                return (
                  <tr key={p.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                    {(platform === 'SELLERHUB' || platform === 'COUPANG' || platform === 'TOSS') && (
                      <td style={td}><input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSelect(p.id)} /></td>
                    )}
                    <td style={td}>
                      <div style={{ width: '42px', height: '42px', borderRadius: '6px', overflow: 'hidden', background: '#f5f5f5' }}>
                        {p.thumbnail && <img src={`https://admin.jinhalife.co.kr${p.thumbnail}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                    </td>
                    <td style={td}>{p.name}<div style={{ fontSize: '10px', color: '#aaa' }}>{p.product_code}</div></td>
                    <td style={td}>{formatDate(status?.sent_at)}</td>
                    <td style={{ ...td, color: '#888' }}><CategoryOverrideCell product={p} platform={platform} cfg={sendConfigs[p.id] || {}} canOverride={false} api={api} matchedName={matchedCategories[p.id]} /></td>
                    <td style={td}>
                      {platform === 'COUPANG' ? (
                        // [버그 수정 - 2026-10-14] 쿠팡은 esm_master_goods_no(지마켓/옥션용)를 저장하지 않고,
                        // 승인상태 확인 시 coupang_vendor_item_id를 저장한다. 이전에는 esm_master_goods_no로
                        // 판단해서 승인완료가 되어도 "전송중(승인 대기)" 문구가 바뀌지 않았다.
                        status?.coupang_vendor_item_id ? (
                          <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#E6F1FB', color: '#185FA5' }}>승인완료 (쿠팡 Wing에서 자동생성옵션 비활성화 확인 후 완료 처리해주세요)</span>
                        ) : (
                          <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#FAEEDA', color: '#854F0B' }}>전송중 (쿠팡 승인 대기, 상품ID: {status?.esm_goods_no})</span>
                        )
                      ) : platform === 'TOSS' ? (
                        <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#FAEEDA', color: '#854F0B' }}>검수 대기중 (상품ID: {status?.esm_goods_no})</span>
                      ) : platform === 'SELLERHUB' ? (
                        status?.esm_goods_no ? (
                          <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#E6F1FB', color: '#185FA5' }}>셀러허브 상품번호 확인완료 (고시정보 등록 후 완료 처리해주세요)</span>
                        ) : (
                          <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#FAEEDA', color: '#854F0B' }}>전송중 (셀러허브 사이트 확인 대기)</span>
                        )
                      ) : (
                        status?.esm_goods_no ? (
                          <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#E6F1FB', color: '#185FA5' }}>{platform === 'AUCTION' ? '옥션' : '지마켓'} 상품번호 확인완료 (미등록항목 자동채우기를 실행해주세요)</span>
                        ) : (
                          <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#FAEEDA', color: '#854F0B' }}>전송중 (ESM 사이트 확인 대기)</span>
                        )
                      )}
                    </td>
                    {platform === 'COUPANG' && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button onClick={() => handleCoupangCheckStatus(p.id)}
                          style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>
                          승인상태 확인
                        </button>
                      </td>
                    )}
                    {platform === 'TOSS' && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button onClick={() => handleTossCheckStatus(p.id)}
                          style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>
                          검수상태 확인
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#fafafa', borderBottom: '0.5px solid #e8e8e8' }}>
              <th style={th}></th><th style={th}>상품명</th><th style={th}>실패시간</th><th style={th}>카테고리</th><th style={th}>실패 사유</th><th style={{ ...th, textAlign: 'right' }}>작업</th>
            </tr></thead>
            <tbody>
              {products.map(p => {
                const status = (p.platform_send_status || []).find(s => s.platform === platform);
                return (
                  <tr key={p.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                    <td style={td}>
                      <div style={{ width: '42px', height: '42px', borderRadius: '6px', overflow: 'hidden', background: '#f5f5f5' }}>
                        {p.thumbnail && <img src={`https://admin.jinhalife.co.kr${p.thumbnail}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                    </td>
                    <td style={td}>{p.name}<div style={{ fontSize: '10px', color: '#aaa' }}>{p.product_code}</div></td>
                    <td style={td}>{formatDate(status?.sent_at)}</td>
                    <td style={{ ...td, color: '#888' }}><CategoryOverrideCell product={p} platform={platform} cfg={sendConfigs[p.id] || {}} canOverride={platform !== 'SMARTSTORE'} api={api} matchedName={matchedCategories[p.id]} onSave={(fields) => saveSendConfigMulti(p.id, fields)} /></td>
                    <td style={{ ...td, color: '#A32D2D', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={status?.fail_reason}>{status?.fail_reason || '-'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        <a href={`/products?search=${encodeURIComponent(p.product_code)}`} target="_blank" rel="noreferrer" style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff', color: '#555', textDecoration: 'none' }}>수정</a>
                        {platform === 'COUPANG' && (
                          <button onClick={() => setBrandProduct(p)} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #7F77DD', background: '#EEEDFE', color: '#3C3489', cursor: 'pointer' }}>
                            브랜드 선택
                          </button>
                        )}
                        {(platform === 'COUPANG' || platform === 'TOSS') && (
                          <button onClick={() => setExtraAttrProduct(p)} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #7F77DD', background: '#EEEDFE', color: '#3C3489', cursor: 'pointer' }}>
                            필수 구매옵션 추가
                          </button>
                        )}
                        <button onClick={() => handleRetry(p)} disabled={retryingId === p.id} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: retryingId === p.id ? '#f0f0f0' : '#E6F1FB', color: retryingId === p.id ? '#aaa' : '#185FA5', cursor: retryingId === p.id ? 'not-allowed' : 'pointer' }}>
                          {retryingId === p.id ? '재전송 중...' : '재전송'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px' }}>
        {/* [기능 강화 - 2026-10-14] 이전/다음만 있어 멀리 이동하기 불편했던 페이지네이션 개선:
            처음/마지막, 현재 페이지 주변 번호 버튼, 페이지 직접 입력 이동, 전체 건수 표시. */}
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
          {(() => {
            const btn = (disabled, active) => ({
              fontSize: '12px', padding: '5px 10px', borderRadius: '6px',
              border: active ? '0.5px solid #7F77DD' : '0.5px solid #ddd',
              background: active ? '#EEEDFE' : '#fff',
              color: active ? '#3C3489' : (disabled ? '#bbb' : '#333'),
              fontWeight: active ? '600' : '400',
              cursor: disabled ? 'not-allowed' : 'pointer', minWidth: '30px'
            });
            const start = Math.max(1, page - 2);
            const end = Math.min(totalPages, page + 2);
            const nums = [];
            if (start > 1) nums.push(1);
            if (start > 2) nums.push('dots-l');
            for (let i = start; i <= end; i++) nums.push(i);
            if (end < totalPages - 1) nums.push('dots-r');
            if (end < totalPages) nums.push(totalPages);
            const goPage = () => {
              const n = parseInt(pageInput, 10);
              if (!isNaN(n)) setPage(Math.min(totalPages, Math.max(1, n)));
              setPageInput('');
            };
            return (
              <>
                <button onClick={() => setPage(1)} disabled={page === 1} style={btn(page === 1, false)}>« 처음</button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={btn(page === 1, false)}>‹ 이전</button>
                {nums.map(n => (typeof n === 'string'
                  ? <span key={n} style={{ fontSize: '12px', color: '#999', padding: '0 2px' }}>…</span>
                  : <button key={n} onClick={() => setPage(n)} style={btn(false, n === page)}>{n}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={btn(page === totalPages, false)}>다음 ›</button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={btn(page === totalPages, false)}>마지막 »</button>
                <input type="number" min="1" max={totalPages} value={pageInput}
                  onChange={e => setPageInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') goPage(); }}
                  placeholder="페이지"
                  style={{ width: '64px', fontSize: '12px', padding: '5px 6px', borderRadius: '6px', border: '0.5px solid #ddd', marginLeft: '6px' }} />
                <button onClick={goPage} style={btn(false, false)}>이동</button>
                <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>{page} / {totalPages} 페이지 · 총 {total}건</span>
                <select value={pageLimit} onChange={e => { setPageLimit(parseInt(e.target.value)); setPage(1); }}
                  style={{ fontSize: '12px', padding: '5px 8px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff', marginLeft: '10px' }}>
                  {[20, 50, 60, 80, 100].map(n => <option key={n} value={n}>{n}개씩 보기</option>)}
                </select>
              </>
            );
          })()}
        </div>
        {EXCEL_PLATFORM_CODES.includes(platform) && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <label style={{ fontSize: '12px', padding: '7px 16px', borderRadius: '8px', border: '0.5px solid #7F77DD', background: '#EEEDFE', color: '#3C3489', cursor: 'pointer', fontWeight: '500' }}>
              확인파일 업로드
              <input type="file" accept=".xlsx,.xls" onChange={handleConfirmFileSelect} style={{ display: 'none' }} />
            </label>
            {ESM_PLATFORM_CODES.includes(platform) && (
              <button onClick={handleFieldFix} disabled={fixRunning}
                style={{ fontSize: '12px', padding: '7px 16px', borderRadius: '8px', border: '0.5px solid #7F77DD', background: fixRunning ? '#f0f0f0' : '#EEEDFE', color: fixRunning ? '#aaa' : '#3C3489', cursor: fixRunning ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
                {fixRunning ? '처리 중...' : '미등록항목 자동채우기'}
              </button>
            )}
            {platform === 'SELLERHUB' && (
              <>
                <button onClick={handleSellerhubNoticeDownload} disabled={sending || selectedIds.length === 0}
                  style={{ fontSize: '12px', padding: '7px 16px', borderRadius: '8px', border: '0.5px solid #7F77DD', background: sending || selectedIds.length === 0 ? '#f0f0f0' : '#EEEDFE', color: sending || selectedIds.length === 0 ? '#aaa' : '#3C3489', cursor: sending || selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
                  고시정보 엑셀 다운로드
                </button>
                <button onClick={handleSellerhubComplete} disabled={sending || selectedIds.length === 0}
                  style={{ fontSize: '12px', padding: '7px 16px', borderRadius: '8px', border: '0.5px solid #1D9E75', background: sending || selectedIds.length === 0 ? '#f0f0f0' : '#E1F5EE', color: sending || selectedIds.length === 0 ? '#aaa' : '#1D9E75', cursor: sending || selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
                  완료
                </button>
              </>
            )}
          </div>
        )}
        {platform === 'COUPANG' && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleCoupangComplete} disabled={sending || selectedIds.length === 0}
              style={{ fontSize: '12px', padding: '7px 16px', borderRadius: '8px', border: '0.5px solid #1D9E75', background: sending || selectedIds.length === 0 ? '#f0f0f0' : '#E1F5EE', color: sending || selectedIds.length === 0 ? '#aaa' : '#1D9E75', cursor: sending || selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
              완료 (자동생성옵션 비활성화 확인 후)
            </button>
          </div>
        )}
      </div>
      {(fixRunning || fixJob) && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>미등록항목 자동채우기 결과</div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', border: '0.5px solid #e8e8e8', borderRadius: '8px', minHeight: '80px' }}>
              {(fixJob?.results || []).map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
                  <span style={{ fontSize: '12px', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: r.success ? '#E1F5EE' : '#FCEBEB', color: r.success ? '#085041' : '#A32D2D', flexShrink: 0, marginLeft: '8px' }}>
                    {r.success ? '완료' : '실패'}
                  </span>
                </div>
              ))}
              {fixRunning && (
                <div style={{ padding: '10px 14px', fontSize: '12px', color: '#aaa' }}>처리 중...</div>
              )}
              {!fixRunning && (fixJob?.results || []).length === 0 && (
                <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>대상 상품이 없습니다</div>
              )}
            </div>
            <button onClick={() => { setFixJob(null); setFixJobId(null); }} disabled={fixRunning}
              style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: fixRunning ? '#ccc' : '#3C3489', color: '#fff', cursor: fixRunning ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
              {fixRunning ? '처리 중...' : '확인'}
            </button>
          </div>
        </div>
      )}
      {(confirmUploading || confirmJob) && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>확인파일 처리 결과</div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', border: '0.5px solid #e8e8e8', borderRadius: '8px', minHeight: '80px' }}>
              {(confirmJob?.results || []).map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
                  <span style={{ fontSize: '12px', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: r.success ? '#E1F5EE' : '#FCEBEB', color: r.success ? '#085041' : '#A32D2D', flexShrink: 0, marginLeft: '8px' }}>
                    {r.success ? '완료' : '실패'}
                  </span>
                </div>
              ))}
              {confirmUploading && (
                <div style={{ padding: '10px 14px', fontSize: '12px', color: '#aaa' }}>처리 중...</div>
              )}
              {!confirmUploading && (confirmJob?.results || []).length === 0 && (
                <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>일치하는 상품이 없습니다</div>
              )}
            </div>
            <button onClick={() => { setConfirmJob(null); setConfirmJobId(null); }} disabled={confirmUploading}
              style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: confirmUploading ? '#ccc' : '#3C3489', color: '#fff', cursor: confirmUploading ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
              {confirmUploading ? '처리 중...' : '확인'}
            </button>
          </div>
        </div>
      )}
      {showCoupangNameCheck && <CoupangNameCheckModal onClose={() => setShowCoupangNameCheck(false)} />}
      {extraAttrProduct && platform === 'COUPANG' && <CoupangExtraAttributesModal product={extraAttrProduct} onClose={() => setExtraAttrProduct(null)} />}
      {extraAttrProduct && platform === 'TOSS' && <TossExtraAttributesModal product={extraAttrProduct} onClose={() => setExtraAttrProduct(null)} />}
      {brandProduct && platform === 'COUPANG' && <CoupangBrandModal product={brandProduct} onClose={() => setBrandProduct(null)} />}
      {unconfirmedBrands && (
        <EsmBrandConfirmModal items={unconfirmedBrands}
          onDone={() => { setUnconfirmedBrands(null); runFieldFix(); }}
          onCancel={() => setUnconfirmedBrands(null)} />
      )}
    </div>
  );
}
